import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { BASE_URL } from "../../Utils/api";
import { Alert, Badge, Button, Card, Col, Modal, Row, Spinner } from "react-bootstrap";
import { ErroModal } from "../Generals";
import "./GodMode.css";

const INTERVALO_POLLING_MS = 3000;

function formatarDataHora(valor) {
  if (!valor) return "—";
  const data = new Date(valor);
  return Number.isNaN(data.getTime()) ? String(valor) : data.toLocaleString("pt-BR");
}

function MigracaoPdfsGodMode() {
  const token = localStorage.getItem("authToken");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [status, setStatus] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [iniciando, setIniciando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [showConfirmar, setShowConfirmar] = useState(false);
  const pollingRef = useRef(null);

  const buscarStatus = useCallback(async () => {
    try {
      const { data } = await api.get(`${BASE_URL}/api/godmode/migracaoPdfs/status`, { headers });
      setStatus(data);
      return data;
    } catch (err) {
      setErro(err.response?.data?.message || "Não foi possível carregar o status da migração.");
      return null;
    } finally {
      setCarregando(false);
    }
  }, [headers]);

  useEffect(() => {
    buscarStatus();
    return () => clearTimeout(pollingRef.current);
  }, [buscarStatus]);

  useEffect(() => {
    if (status?.em_andamento) {
      pollingRef.current = setTimeout(buscarStatus, INTERVALO_POLLING_MS);
    }
    return () => clearTimeout(pollingRef.current);
  }, [status, buscarStatus]);

  const confirmarInicio = async () => {
    setShowConfirmar(false);
    setIniciando(true);
    setErro("");
    setSucesso("");
    try {
      const { data } = await api.post(`${BASE_URL}/api/godmode/migracaoPdfs/iniciar`, {}, { headers });
      setSucesso(data?.message || "Migração iniciada.");
      await buscarStatus();
    } catch (err) {
      setErro(err.response?.data?.message || "Não foi possível iniciar a migração de PDFs.");
    } finally {
      setIniciando(false);
    }
  };

  const emAndamento = Boolean(status?.em_andamento);
  const nuncaExecutado = Boolean(status?.nunca_executado);

  return (
    <div>
      <Card className="mb-3 godmode-clientes-card">
        <Card.Body>
          <Row className="align-items-center mb-3">
            <Col>
              <h5 className="mb-1">Migração de PDFs para o banco (item #31)</h5>
              <p className="text-muted mb-0">
                Copia os PDFs hoje salvos em disco (uploads/) para o banco de dados, documento por documento. Os
                arquivos originais não são apagados nem alterados.
              </p>
            </Col>
            <Col xs="auto">
              <Button
                variant="primary"
                onClick={() => setShowConfirmar(true)}
                disabled={carregando || iniciando || emAndamento}
              >
                {iniciando && <Spinner animation="border" size="sm" className="me-2" />}
                {emAndamento ? "Migração em andamento..." : "Iniciar migração"}
              </Button>
            </Col>
          </Row>

          <ErroModal erro={erro} />
          {sucesso && <Alert variant="success">{sucesso}</Alert>}

          {carregando ? (
            <Spinner animation="border" size="sm" />
          ) : nuncaExecutado ? (
            <p className="text-muted mb-0">Nenhuma migração foi executada ainda.</p>
          ) : (
            status && (
              <>
                <Row className="g-3 mb-2">
                  <Col md={2}>
                    <div className="text-muted small">Status</div>
                    <Badge bg={emAndamento ? "warning" : status.erro > 0 ? "danger" : "success"}>
                      {emAndamento ? "Em andamento" : status.erro > 0 ? "Concluída com erros" : "Concluída"}
                    </Badge>
                  </Col>
                  <Col md={2}>
                    <div className="text-muted small">Candidatos</div>
                    <strong>{status.total_candidatos}</strong>
                  </Col>
                  <Col md={2}>
                    <div className="text-muted small">Processados</div>
                    <strong>{status.processados}</strong>
                  </Col>
                  <Col md={2}>
                    <div className="text-muted small">Sucesso</div>
                    <strong className="text-success">{status.sucesso}</strong>
                  </Col>
                  <Col md={2}>
                    <div className="text-muted small">Erros</div>
                    <strong className={status.erro > 0 ? "text-danger" : ""}>{status.erro}</strong>
                  </Col>
                </Row>
                <Row className="g-3">
                  <Col md={4}>
                    <div className="text-muted small">Iniciado em</div>
                    <div>{formatarDataHora(status.iniciado_em)}</div>
                  </Col>
                  <Col md={4}>
                    <div className="text-muted small">Finalizado em</div>
                    <div>{formatarDataHora(status.finalizado_em)}</div>
                  </Col>
                </Row>
                {status.ultimo_erro && (
                  <Alert variant="warning" className="mt-3 mb-0">
                    <strong>Último erro:</strong> {status.ultimo_erro}
                  </Alert>
                )}
              </>
            )
          )}
        </Card.Body>
      </Card>

      <Modal show={showConfirmar} onHide={() => setShowConfirmar(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Iniciar migração de PDFs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Isso vai ler todos os PDFs referenciados por documentos/aditivos ainda não migrados e copiá-los para o
          banco de dados. Os arquivos originais em disco não são apagados nem alterados. Pode levar alguns minutos
          dependendo da quantidade de arquivos. Confirma o início?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmar(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={confirmarInicio}>
            Confirmar migração
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default MigracaoPdfsGodMode;
